/**
 * HttpTaskInspector — Phase 5a-3, the first per-node-type editor in V4.
 *
 * Edits HTTP Request task config. Fields:
 *   url            string         the endpoint to call
 *   method         enum           GET | POST | PUT | DELETE | PATCH
 *   timeoutMs      number         request timeout in ms (optional)
 *   headers        string         raw JSON object (textarea) — Phase 5a-3
 *                                  uses a textarea; a key-value editor is a
 *                                  Phase 6 polish item
 *   body           string         request body (textarea, JSON or text)
 *
 * Mutation contract per useCanvas:1098-1109 — when updating `config`, we
 * MUST spread the existing config object so V3-only fields that V4 doesn't
 * render (outputMapping, retryPolicy, etc.) are preserved through a save.
 * The single-field helper below handles that.
 */

import React from 'react';

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

export default function HttpTaskInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const url = cfg.url || '';
  const method = (cfg.method || 'GET').toUpperCase();
  const timeoutMs = cfg.timeoutMs || cfg.timeout || '';
  const headers = stringifyHeaders(cfg.headers);
  const body = typeof cfg.body === 'string' ? cfg.body : (cfg.body != null ? JSON.stringify(cfg.body, null, 2) : '');

  // Single-field mutation helper — spreads the existing config so unknown
  // V3-only fields survive the save round-trip. See useCanvas:1098.
  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup label="URL" hint="The endpoint to call.">
        <input
          type="text"
          value={url}
          onChange={(e) => setField('url', e.target.value)}
          placeholder="https://api.example.com/v1/widgets"
          disabled={readOnly}
          style={inputStyle}
        />
      </FieldGroup>

      <FieldGroup label="Method">
        <select
          value={METHODS.includes(method) ? method : 'GET'}
          onChange={(e) => setField('method', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup label="Timeout (ms)" hint="Request timeout. Blank = backend default.">
        <input
          type="number"
          min="0"
          step="100"
          value={timeoutMs}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') setField('timeoutMs', undefined);
            else { const n = Number(v); if (Number.isFinite(n)) setField('timeoutMs', n); }
          }}
          placeholder="30000"
          disabled={readOnly}
          style={inputStyle}
        />
      </FieldGroup>

      <FieldGroup label="Headers" hint="JSON object — a key-value editor is on the roadmap.">
        <textarea
          value={headers}
          onChange={(e) => setField('headers', parseHeadersInput(e.target.value))}
          placeholder='{ "Authorization": "Bearer ..." }'
          disabled={readOnly}
          rows={3}
          style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11, lineHeight: 1.4 }}
        />
      </FieldGroup>

      <FieldGroup label="Body" hint="Request body. JSON for POST/PUT/PATCH.">
        <textarea
          value={body}
          onChange={(e) => setField('body', e.target.value)}
          placeholder='{ "key": "value" }'
          disabled={readOnly}
          rows={6}
          style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11, lineHeight: 1.4 }}
        />
      </FieldGroup>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────── */

function stringifyHeaders(headers) {
  if (!headers) return '';
  if (typeof headers === 'string') return headers;
  try { return JSON.stringify(headers, null, 2); } catch (_) { return String(headers); }
}

// Try to parse the textarea content as JSON; if it parses, store the
// object; if not (mid-typing), store the raw string. Saves the user
// from a syntax error blowing up their work.
function parseHeadersInput(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return undefined;
  try { return JSON.parse(raw); } catch (_) { return raw; }
}

/* ───── shared field UI ───────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'auto',
};
